<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-2xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="my-5 filter-container"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="3" />
      <vs-col vs-w="2">
        <select-with-search
          :hide-search="true"
          v-model="filters.owner_id"
          placeholder="Select Owner Name"
          class="px-2"
          :options="ownerOptions"
        />
      </vs-col>
      <vs-col vs-w="2">
        <select-with-search
          :hide-search="true"
          v-model="filters.route_name"
          placeholder="Select Route Name"
          class="px-2"
          :options="routeOptions"
        />
      </vs-col>
      <vs-col vs-w="1.5">
        <date-picker
          v-model="filters.month"
          :format="'MMM'"
          type="month"
          value-type="M"
          placeholder="Month"
          class="px-2"
        />
      </vs-col>
      <vs-col vs-w="1.5">
        <date-picker
          v-model="filters.year"
          :format="'YYYY'"
          type="year"
          value-type="YYYY"
          placeholder="Year"
          class="px-2"
        />
      </vs-col>
      <vs-col
        vs-w="1"
        class="flex flex-col justify-center"
      >
        <vs-button
          type="border"
          color="primary"
          class="self-center font-semibold"
          style="min-width: 100px;"
          @click="resetFilters"
        >
          Reset
        </vs-button>
      </vs-col>
    </vs-row>
    <div class="mt-3 font-semibold">
      Fuel Consumption Summary
    </div>
    
    <!-- Top Row -->
    <div
      v-if="isBusy"
      class="p-3 w-full flex flex-col gap-5 bg-white rounded-lg shadow-md mt-3"
    >
      <vs-progress
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
    </div>
    <div
      v-if="!isBusy"
      class="grid grid-cols-3 gap-3 mt-3"
    >
      <div class="bg-white rounded-lg shadow p-4 text-center">
        <h3 class="text-left text-sm font-semibold">
          Saving Cost
        </h3>
        <p
          class="text-green-600 font-bold text-2xl mt-3"
          :style="{
            color:
              fuelConsumptionSummary.fuel_consumption_actual > fuelConsumptionSummary.fuel_quota
                ? '#FF3662' : '#33B57D'
          }"
        >
          {{ handleValidateAndFormatNumber(fuelConsumptionSummary.saving_cost) }} <span class="text-sm font-medium text-black">USD/Ton</span>
        </p>
      </div>

      <div class="bg-white rounded-lg shadow p-4 text-center">
        <h3 class="text-left text-sm font-semibold">
          Fuel Reduction Saved
        </h3>
        <div class="flex items-center justify-center mt-3">
          <span
            class="ml-2 text-white px-3 py-2 rounded-md flex items-center text-lg"
            :style="{
              backgroundColor: fuelConsumptionSummary.reduction_saved < 0 ? '#FF3662' : '#33B57D'
            }"
          >
            <vs-icon
              :icon="fuelConsumptionSummary.reduction_saved < 0 ? 'trending_up' : 'trending_down'"
              color="#fff"
              size="small"
            />
            {{ handleValidateAndFormatNumber(fuelConsumptionSummary.reduction_saved) }}%
          </span>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-4 text-center">
        <h3 class="text-left text-sm font-semibold">
          Potential
        </h3>
        <p class="text-black font-bold text-2xl mt-3">
          {{ handleValidateAndFormatNumber(fuelConsumptionSummary.potential) }} <span class="text-sm font-medium">USD/Ton</span>
        </p>
      </div>
    </div>

    <!-- Bottom Row -->
    <div
      v-if="!isBusy"
      class="grid grid-cols-2 gap-3 mt-3"
    >
      <div class="bg-white rounded-lg shadow p-4">
        <h3 class="text-sm font-semibold">
          Fuel Consumption
        </h3>
        <vs-row
          vs-w="12"
          class="flex items-center mt-2 text-xl font-bold"
        >
          <vs-col
            vs-w="6"
            class="flex justify-end items-center"
          >
            <div class="text-center mr-5">
              <p class="text-base font-normal">
                Plan
              </p>
              <div class="my-2 flex items-center justify-center">
                <span>{{ handleValidateAndFormatNumber(fuelConsumptionSummary.fuel_consumption_plan) }}</span>
              </div>
              <p class="text-sm font-normal">
                Liter
              </p>
            </div>
            <div class="border-r border-gray-400 h-8" />
          </vs-col>
          <vs-col
            vs-w="6"
            class="flex justify-start items-center"
          >
            <div class="text-center ml-5">
              <p class="text-base font-normal">
                Actual
              </p>
              <div class="my-2 flex items-center">
                <span>{{ handleValidateAndFormatNumber(fuelConsumptionSummary.fuel_consumption_actual) }}</span>
                <span
                  class="ml-2 text-white p-1 rounded-md flex items-center text-sm"
                  :style="{
                    backgroundColor: fuelConsumptionSummary.fuel_consumption_comparison < 0 ? '#FF3662' : '#33B57D'
                  }"
                >
                  <vs-icon
                    :icon="fuelConsumptionSummary.fuel_consumption_comparison < 0 ? 'trending_up' : 'trending_down'"
                    color="#fff"
                    size="small"
                  />
                  {{ handleValidateAndFormatNumber(fuelConsumptionSummary.fuel_consumption_comparison) }}%
                </span>
              </div>
              <p class="text-sm font-normal">
                Liter
              </p>
            </div>
          </vs-col>
        </vs-row>
      </div>

      <div class="bg-white rounded-lg shadow p-4">
        <h3 class="text-sm font-semibold">
          Fuel Cost
        </h3>
        <vs-row
          vs-w="12"
          class="flex items-center mt-2 text-xl font-bold"
        >
          <vs-col
            vs-w="6"
            class="flex justify-end items-center"
          >
            <div class="text-center mr-5">
              <p class="text-base font-normal">
                Plan
              </p>
              <div class="my-2 flex items-center justify-center">
                <span>{{ handleValidateAndFormatNumber(fuelConsumptionSummary.fuel_cost_plan) }}</span>
              </div>
              <p class="text-sm font-normal">
                USD
              </p>
            </div>
            <div class="border-r border-gray-400 h-8" />
          </vs-col>
          <vs-col
            vs-w="6"
            class="flex justify-start items-center"
          >
            <div class="text-center ml-5">
              <p class="text-base font-normal">
                Actual
              </p>
              <div class="my-2 flex items-center">
                <span>{{ handleValidateAndFormatNumber(fuelConsumptionSummary.fuel_cost_actual) }}</span>
                <span
                  class="ml-2 text-white p-1 rounded-md flex items-center text-sm"
                  :style="{
                    backgroundColor: fuelConsumptionSummary.fuel_cost_comparison < 0 ? '#FF3662' : '#33B57D'
                  }"
                >
                  <vs-icon
                    :icon="fuelConsumptionSummary.fuel_cost_comparison < 0 ? 'trending_up' : 'trending_down'"
                    color="#fff"
                    size="small"
                  />
                  {{ handleValidateAndFormatNumber(fuelConsumptionSummary.fuel_cost_comparison) }}%
                </span>
              </div>
              <p class="text-sm font-normal">
                USD
              </p>
            </div>
          </vs-col>
        </vs-row>
      </div>
    </div>
    <div class="mt-3 p-8 bg-white rounded-lg shadow ">
      <h2 class="text-lg font-semibold mb-4">
        Plan & Actual Fuel Consumption
      </h2>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <BarChart
        :data="planActualFuelConsumptionData"
        :options="planActualChartOptions"
        :plugins="chartPlugins.bar"
      />
    </div>
    <div
      v-if="isBusy"
      class="p-3 w-full flex flex-col gap-5 bg-white rounded-lg shadow-md mt-3"
    >
      <vs-progress
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
    </div>
    <div
      v-else
      class="mt-3 grid grid-cols-3 gap-3"
    >
      <MetricCard
        title="Volume Based 5% Fuel Reduction"
        :value="ownerFuelOverview.volume_base_percent"
        unit="USD/Ton"
      />
      <MetricCard
        title="Distance Based 5% Fuel Reduction"
        :value="ownerFuelOverview.distance_base_percent"
        unit="USD/Nm"
      />
      <MetricCard
        title="Top Quartile for barge Cycle"
        :value="ownerFuelOverview.top_quartile_barge_cycle"
        unit="Days"
      />
      <MetricCard
        title="Liter/Ton"
        :value="ownerFuelOverview.liter_per_ton"
        unit=""
      />
      <MetricCard
        title="Liter/Nm"
        :value="ownerFuelOverview.liter_per_nm"
        unit="liter/Nm"
      />
      <MetricCard
        title="Cargo Capacity Utilization"
        :value="ownerFuelOverview.cargo_capacity_utilization"
        unit="%"
      />
    </div>
    <div
      v-if="!isBusy"
      class="mt-3"
    >
      <SummaryTable
        :table-data="tableData"
        :owner-fuel-overview="ownerFuelOverview"
        :ytd="filters.year"
      />
    </div>
    <div class="mt-3 font-semibold">
      Fuel Bunkering Summary
    </div>
    <div class="mt-3 p-4 bg-white rounded-lg shadow">
      <div class="flex justify-between">
        <h2 class="text-lg font-semibold mb-4">
          Bunkering Time (hr/trip)
        </h2>
        <h4 class="font-semibold">
          Number of trips: {{ totalTrip }} trips
        </h4>
      </div>
      
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <BarChart
        v-else
        :data="chartFuelBunker"
        :options="fuelBunkerOptions"
        :plugins="[chartPlugins.bar[0]]"
      />
    </div>
    <div class="mt-3 p-4 bg-white rounded-lg shadow">
      <h2 class="text-lg font-semibold mb-4">
        Waiting Time for Bunkering
      </h2>
      <vs-progress
        v-if="isBusy"
        indeterminate
        color="primary"
      >
        primary
      </vs-progress>
      <div
        v-else
        class="flex flex-col md:flex-row gap-4"
      >
        <div class="p-4 w-full md:w-1/3 items-center">
          <DoughnutChart
            :data="waitingTimeBunker.pieChart"
            :plugins="chartPlugins.doughnut"
            :options="waitingTimeBunkerPieOptions"
          />
        </div>
        <div class="w-full md:w-2/3">
          <BarChart
            :data="waitingTimeBunker.barChart"
            :options="waitingTimeBarOptions"
            :height="360"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import BarChart from "@/components/barChart"
import DatePicker from "@/components/datePicker"
import DoughnutChart from "@/components/doughnutChart"
import MetricCard from "@/components/metricCard"
import SelectWithSearch from "@/components/selectWithSearch";
import { isNumber } from '@/utils/salesPlanUtils'

import SummaryTable from "./SummaryTable.vue";

export default {
  components: {
    MetricCard,
    BarChart,
    DoughnutChart,
    SummaryTable,
    DatePicker,
    SelectWithSearch
  },
  data() {
    const currentYear = this.moment().format('YYYY')
    return {
      isBusy: true,
      usedColors: new Set(),
      filters: {
        owner_id: null,
        route_name: null,
        month: null,
        year: currentYear
      },
      ownerOptions: [],
      routeOptions: [],
      fuelConsumptionSummary: {
        fuel_consumption_actual: 0,
        fuel_consumption_comparison: 0,
        fuel_consumption_plan: 0,
        fuel_cost_actual: 0,
        fuel_cost_comparison: 0,
        fuel_cost_plan: 0,
        potential: 0,
        reduction_saved: 0,
        saving_cost: 0,
        fuel_quota: 0
      },
      planActualFuelConsumptionData: {
        labels: [],
        datasets: []
      },
      ownerFuelOverview: {
        cargo_capacity_utilization: 0,
        distance_base_percent: 0,
        liter_per_nm: 0,
        liter_per_ton: 0,
        top_quartile_barge_cycle: 0,
        volume_base_percent: 0
      },
      tableData: [],
      totalTrip: 0,
      chartFuelBunker: {
        labels: [],
        datasets: []
      },
      waitingTimeBunker: {
        pieChart: {
          labels: [],
          datasets: []
        },
        barChart: {
          labels: [],
          datasets: []
        }
      },
      maxValue: 500
    }
  },
  computed: {
    ...mapGetters({
      routes: 'Dashboard/routeOpt',
      owners: 'Dashboard/ownerOpt',
      fuelSupplyUtilizationData: 'FuelSupplyManagement/fuelSupplyUtilizationData'
    }),
    chartPlugins() {
      return {
        doughnut: [this.drawPercentagePlugin(), this.centerTextPlugin()],
        bar: [this.drawBarValuePlugin(), this.drawBoxValuePlugin()]
      }
    },
    planActualChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          labels: {
            fontColor: '#333',
            boxWidth: 12
          }
        },
        layout: {
          padding: {
            top: 30,
            right: 30
          }
        },
        scales: {
          y: {
            min: 0,
            max: this.maxValue // set your max value here
          }
        }
      }
    },
    fuelBunkerOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'Hours',
              font: { size: 14 }
            }
          }
        },
        layout: {
          padding: {
            top: 30,
            right: 30
          }
        },
        plugins: {
          legend: { position: 'bottom' },
          tooltip: {
            callbacks: {
              label: (tooltipItem) => `${tooltipItem.dataset.label}: ${tooltipItem.raw}`
            }
          }
        }
      }
    },
    waitingTimeBunkerPieOptions() {
      return {
        cutout: '60%',
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: (context) => `${context.label}: ${context.parsed.toFixed(2)}%`
            }
          }
        }
      }
    },
    waitingTimeBarOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            stacked: true
          },
          y: {
            stacked: true,
            title: {
              display: true,
              text: 'Hours'
            },
            ticks: {
              stepSize: 5
            },
            grid: {
              drawTicks: false
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true
          },
          // Mode Full Overview All Stacked Data
          // tooltip: {
          //   mode: 'index',
          //   intersect: false
          // },
          annotation: {
            annotations: {
              limitLine: {
                type: 'line',
                yMin: 20,
                yMax: 20,
                borderColor: 'red',
                borderDash: [5, 5],
                borderWidth: 2,
                label: {
                  display: false
                }
              }
            }
          }
        }
      }
    }
  },
  watch: {
    owners: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.ownerOptions = val.map(item => ({ text: item.owner_name, value: item.id }))
        }
      }
    },
    routes: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.routeOptions = val.map(item => ({ text: item, value: item }))
        }
      }
    },
    fuelSupplyUtilizationData: {
      handler(val) {
        if (!this.isEmpty(val)) {
          this.fuelConsumptionSummary = val?.fuel_consumption_summary

          this.maxValue = Math.max(
            ...val?.plan_comparison_actual?.map(v => v?.plan),
            ...val?.plan_comparison_actual?.map(v => v?.actual)
          ) + 500
          this.planActualFuelConsumptionData.labels =
            [
              ...this.populateDataMonth(
                val?.plan_comparison_actual?.length - 1, val?.plan_comparison_actual?.[0]?.month
              ),
              'MTD'
            ]
          this.planActualFuelConsumptionData.datasets = [
            {
              label: 'Plan',
              backgroundColor: '#f59e0b',
              data: val?.plan_comparison_actual?.map(v => v?.plan),
              saving_cost: val?.plan_comparison_actual?.map(v => v?.saving_cost),
              reduction_saved: val?.plan_comparison_actual?.map(v => v?.reduction_saved)
            },
            {
              label: 'Actual',
              backgroundColor: '#3b82f6',
              data: val?.plan_comparison_actual?.map(v => v?.actual),
              saving_cost: val?.plan_comparison_actual?.map(v => v?.saving_cost),
              reduction_saved: val?.plan_comparison_actual?.map(v => v?.reduction_saved)
            }
          ]

          this.ownerFuelOverview = val?.owner_fuel_overview
          this.tableData = val?.owner_fuel_performance
          this.totalTrip = val?.chart_fuel_bunker?.reduce((sum, item) => sum += item?.total_trip, 0)
          this.chartFuelBunker.labels = [
            ...this.populateDataMonth(
              val?.chart_fuel_bunker?.length - 1,
              val?.chart_fuel_bunker?.[0]?.month
            ),
            'MTD'
          ]
          this.chartFuelBunker.datasets = [
            {
              label: '< Target',
              data: val?.chart_fuel_bunker
                ?.map(v => {
                  if (v?.bunker_time <= v?.line_sta) {
                    return v?.bunker_time
                  }
                  return 0
                }),
              backgroundColor: '#4CAF50', // green
              stack: 'stack1',
              borderRadius: 4,
              barThickness: 24,
              order: 1
            },
            {
              label: '> Target',
              data: val?.chart_fuel_bunker
                ?.map(v => {
                  if (v?.bunker_time > v?.line_sta) {
                    return v?.bunker_time
                  }
                  return 0
                }),
              backgroundColor: '#F43F5E', // rose
              stack: 'stack1',
              borderRadius: 4,
              barThickness: 24,
              order: 1
            },
            {
              label: 'Standard 1hr / Trip',
              data: val?.chart_fuel_bunker?.map(v => v?.line_sta),
              type: 'line',
              borderColor: '#1D4ED8',
              borderDash: [5, 5],
              tension: 0.4,
              pointRadius: 5,
              fill: false,
              yAxisID: 'y',
              order: 0
            }
          ]

          const tempBukerTimeData = val?.pie_chart_waiting_bunker_time?.data
          this.waitingTimeBunker.pieChart = {
            labels: tempBukerTimeData?.map(v => v?.owner_name),
            datasets: [{
              data: tempBukerTimeData?.map(v => v?.percentage),
              backgroundColor: tempBukerTimeData?.map(_ => this.getRandomColor(1)),
              total_hours: val?.pie_chart_waiting_bunker_time?.total_hours
            }]
          }

          this.waitingTimeBunker.barChart = {
            labels: [
              ...this.populateDataMonth(
                val?.chart_waiting_bunker_time?.length - 1,
                val?.chart_waiting_bunker_time?.[0]?.month
              ),
              'MTD'
            ],
            datasets: [
              ...val?.chart_waiting_bunker_time?.[0]?.data?.map(v => ({
                label: v?.name,
                data: val?.chart_waiting_bunker_time?.map(d => d?.data?.find(k => k?.name === v?.name)?.total_waiting_bunker_time_hours),
                backgroundColor: this.getRandomColor(1),
                stack: 'Stack 0',
                barThickness: 24
              }))
            ]
          }
        }
      }
    },
    filters: {
      deep: true,
      handler(val) {
        if (val.month || val.year) {
          this.fetchOwners()
          this.fetchRoutes()
        }
        this.fetchFuelSupplyUtilization()
      }
    }
  },
  mounted() {
    this.fetchFuelSupplyUtilization()
    this.fetchOwners()
    this.fetchRoutes()
  },
  methods: {
    async fetchFuelSupplyUtilization() {
      this.isBusy = true
      try {
        const apiFilters = Object.fromEntries(
          Object.entries(this.filters || {}).filter(([_, value]) => !!value)
        )
        await this.$store.dispatch('FuelSupplyManagement/fetchFuelSupplyUtilization', apiFilters)
        this.isBusy = false
      } catch (error) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to get Fuel Supply Management Utilization Data, please try again')
      }
    },
    async fetchOwners() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getOwnerOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Owner, please try again')
      }
    },
    async fetchRoutes() {
      try {
        const payload = {
          month: this.filters.month ? parseInt(this.filters.month) : null,
          year: this.filters.year ? parseInt(this.filters.year) : null
        }
        await this.$store.dispatch('Dashboard/getRouteOpt', payload)
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Routes, please try again')
      }
    },
    drawBarValuePlugin() {
      return {
        id: 'valueLabel',
        afterDatasetsDraw: chart => {
          const ctx = chart.ctx;
          ctx.save();
          chart.data.datasets.forEach((dataset, dsIdx) => {
            const meta = chart.getDatasetMeta(dsIdx);
            meta.data.forEach((bar, i) => {
              const value = dataset.data[i];
              const barWidth = bar.x - bar.base;
              ctx.font = 'semibold 16px Poppins';
              
              const text = this.handleValidateAndFormatNumber(value);
              const textWidth = ctx.measureText(text).width;
              let color, align;
              if (barWidth > textWidth + 16) {
                color = '#000';
                align = 'center';
              } else {
                color = '#222';
                align = 'center';
              }
              ctx.fillStyle = color;
              ctx.textAlign = align;
              ctx.textBaseline = 'middle';
              if (dataset.type !== 'line' && value > 0)
                ctx.fillText(text, bar.x, bar.y - 12);
            });
          });
          ctx.restore();
        }
      };
    },
    drawBoxValuePlugin() {
      return {
        id: 'boxLabel',
        afterDatasetsDraw: chart => {
          const ctx = chart.ctx;
          ctx.save();
          chart.data.datasets.forEach((dataset, dsIdx) => {
            const meta = chart.getDatasetMeta(dsIdx);
            meta.data.forEach(async (bar, i) => {
              const value = dataset.data[i];
              const reduction_saved = dataset.reduction_saved?.[i]

              const boxWidth = 86;
              const boxHeight = 22;
              const boxX = bar.x - boxWidth / 2;
              const boxY = bar.y - 45;

              let trendingDownIcon;
              if (reduction_saved >= 0 ) {
                ctx.fillStyle = '#33B57D'
                trendingDownIcon = require('!!raw-loader!../../../assets/shipo-icon-trending-down.svg').default
              } else {
                ctx.fillStyle = '#FF3662'
                trendingDownIcon = require('!!raw-loader!../../../assets/shipo-icon-trending-up.svg').default
              }
              if (value > 0)
                this.drawRoundedRect(ctx, boxX, boxY, boxWidth, boxHeight, 6);

              // Draw the random image as icon (if loaded)
              const iconSize = 16;
              const svgBlob = new Blob([trendingDownIcon], { type: 'image/svg+xml' });
              const url = URL.createObjectURL(svgBlob);

              const randomImg = new Image();
              
              // Draw percentage text next to the icon
              ctx.font = 'semibold 12px Poppins';
              ctx.fillStyle = '#fff';
              ctx.textAlign = 'left';
              ctx.textBaseline = 'middle';
              if (value > 0) {
                ctx.fillText(`${this.handleValidateAndFormatNumber(reduction_saved || 0)} %`, boxX + 28, boxY + boxHeight / 2);
                randomImg.onload = () => {
                  ctx.drawImage(randomImg, boxX + 6, boxY + (boxHeight - iconSize) / 2, iconSize, iconSize);
                  URL.revokeObjectURL(url); // cleanup
                };
                randomImg.src = url;
              }
            });
          });
          ctx.restore();
        }
      };
    },
    drawPercentagePlugin() {
      return {
        id: 'drawPercentage',
        afterDatasetsDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;
          const total = data.datasets[0].data.reduce((a, b) => a + b, 0);
          const meta = chart.getDatasetMeta(0);

          meta.data.forEach((arc, index) => {
            const value = data.datasets[0].data[index];
            const calculated = ((value / total) * 100).toFixed(1)
            const percentage = `${calculated}%`;

            const {x, y} = arc.tooltipPosition(); // center of each arc

            ctx.save();
            ctx.fillStyle = '#fff';
            ctx.font = '600 12px Poppins';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(percentage, x, y);
            ctx.restore();
          });
        }
      }
    },
    centerTextPlugin() {
      return {
        id: 'centerText',
        afterDatasetsDraw(chart) {
          const {ctx, chartArea: {width, height}, data} = chart;

          ctx.restore();
          const fontSize = (height / 154).toFixed(2);
          ctx.font = `${fontSize  }em sans-serif`;
          ctx.textBaseline = "middle";

          const totalHours = data.datasets[0].total_hours
          const text = `${totalHours.toFixed(2)} hrs`; // You can make this dynamic
          const textX = Math.round((width - ctx.measureText(text).width) / 2);
          const textY = height / 2;

          ctx.fillText(text, textX, textY);
          ctx.save();
        }
      }
    },
    drawRoundedRect(ctx, x, y, width, height, radius) {
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
      ctx.fill();
    },
    populateDataMonth(length, startFrom = 1) {
      if (!length) {
        return []
      }
      return Array.from({ length }, (_, i) =>
        this.moment().month((startFrom - 1 + i) % 12).format('MMM')
      )
    },
    getRandomColor(alpha = 0.7) {
      let color;
      do {
        const r = Math.floor(Math.random() * 156) + 100;
        const g = Math.floor(Math.random() * 156) + 100;
        const b = Math.floor(Math.random() * 156) + 100;
        color = `rgba(${r},${g},${b},${alpha})`;
      } while (this.usedColors.has(color));

      this.usedColors.add(color);
      return color;
    },
    resetFilters() {
      this.filters = {
        owner_id: null,
        route_name: null,
        month: null,
        year: this.moment().format('YYYY')
      }
    }
  }
};
</script>

<style lang='scss' scoped>
.filter-container {
  @apply w-full p-3 mt-5 flex flex-row items-center bg-white rounded-lg;
}
</style>
